import React, { useMemo } from 'react'

import { Tooltip, VStack } from 'govwell-ui'
import { CSLBContractorDataChangeFragment } from 'types/graphql'

import Tag from 'src/components/Tag'
import Text, { TextSize } from 'src/components/Typography/Text'
import { DateFormats, formatDate } from 'src/utils/date'

type Props = {
  dataChanges: CSLBContractorDataChangeFragment[]
  snapshotDate?: string | null
  latestDataDate?: string | null
}

const CSLBContractorDataChangesAlert = ({ dataChanges, snapshotDate, latestDataDate }: Props) => {
  const content = useMemo(
    () => (
      <VStack gap={2} width="auto">
        <Text size={TextSize.Base} className="font-bold">
          The CSLB data on this record is a snapshot of data from the time of application. The data
          has since changed:
        </Text>
        {snapshotDate && (
          <Text size={TextSize.Small}>
            CSLB data from time of application:{' '}
            <span className="font-semibold">
              {formatDate(snapshotDate, DateFormats.MonthNameDateNoTime)}
            </span>
          </Text>
        )}
        {latestDataDate && (
          <Text size={TextSize.Small}>
            New CSLB data from{' '}
            <span className="font-semibold">
              {formatDate(latestDataDate, DateFormats.MonthNameDateNoTime)}
            </span>
          </Text>
        )}
        {dataChanges.map((change) => (
          <VStack key={change.field} gap={0} width="auto">
            <Text size={TextSize.Small} className="font-semibold">
              {change.field}
            </Text>
            <Text size={TextSize.Small}>
              <span className="text-gray-500 line-through">{change.oldValue || '(empty)'}</span>
              {' → '}
              <span className="font-medium">{change.newValue || '(empty)'}</span>
            </Text>
          </VStack>
        ))}
      </VStack>
    ),
    [dataChanges, snapshotDate, latestDataDate]
  )

  return (
    <Tooltip content={content} side="top" contentClassName="max-w-xs">
      <div className="cursor-pointer">
        <Tag color="blue">Data has changed</Tag>
      </div>
    </Tooltip>
  )
}

export default React.memo(CSLBContractorDataChangesAlert)
